/**
 * ThemeContext.tsx
 * Manages light/dark theme mode with user profile persistence via backend API.
 * Falls back to localStorage for offline/unauthenticated users.
 * Provides theme toggle functionality throughout the app.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  loadThemeFromUser: (userThemePreference: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Default to dark mode, but check localStorage for user preference
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('themeMode') as ThemeMode | null;
    return savedMode || 'dark'; // Default to dark
  });
  
  // Track if we've synced with backend to prevent duplicate updates
  const [isSyncingWithBackend, setIsSyncingWithBackend] = useState(false);

  // Persist theme mode to localStorage and backend whenever it changes
  useEffect(() => {
    if (isSyncingWithBackend) {
      // Skip update if we're currently loading from backend
      return;
    }
    
    // Always update localStorage for offline support
    localStorage.setItem('themeMode', mode);
    console.log(`[Theme] Mode changed to: ${mode}`);
    
    // Try to persist to backend if authenticated
    const updateBackend = async () => {
      try {
        // Check if we have an auth token
        const authToken = api.defaults.headers.common['Authorization'];
        if (!authToken) {
          console.log('[Theme] Not authenticated - skipping backend sync');
          return;
        }
        
        await api.put('/users/profile/', { theme_preference: mode });
        console.log(`[Theme] Synced to backend: ${mode}`);
      } catch (error) {
        console.warn('[Theme] Failed to sync with backend (will use localStorage):', error);
      }
    };
    
    updateBackend();
  }, [mode, isSyncingWithBackend]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const setThemeMode = (newMode: ThemeMode) => {
    setMode(newMode);
  };
  
  /**
   * Load theme from user profile (called by AuthContext after login)
   */
  const loadThemeFromUser = (userThemePreference: ThemeMode) => {
    setIsSyncingWithBackend(true);
    setMode(userThemePreference);
    localStorage.setItem('themeMode', userThemePreference);
    console.log(`[Theme] Loaded from user profile: ${userThemePreference}`);
    // Reset sync flag after a brief delay
    setTimeout(() => setIsSyncingWithBackend(false), 100);
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setThemeMode, loadThemeFromUser }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

